/**
 * 「关于我们」页内容。
 *
 * 品牌定位与研发背景两段沿用创始人的原始表述，只做断句与语气收敛，不添加未确认的履历与数据。
 */
import type { IconName } from './icons';
import type { ActionLink } from './home';

export interface AboutIntro {
  eyebrow: string;
  title: string;
  lead: string;
  actions: readonly ActionLink[];
}

export const ABOUT_INTRO: AboutIntro = {
  eyebrow: '关于我们',
  title: '从一台自研四轴，到一套监管平台',
  lead: 'Havorix™ 的起点，是一台从零设计的 H 型 770 轴距无人机。此后我们沿同一条路径向上延伸，构建监管平台与配套软硬件。目标朴素而明确：做一套安全可靠、操作简易、全程可追溯的系统，为各类低空运营提供稳固的基础。',
  actions: [
    { label: '了解平台能力', to: '/platform', variant: 'primary' },
    { label: '联系我们', to: '/contact', variant: 'secondary' },
  ],
};

export interface Positioning {
  icon: IconName;
  title: string;
  text: string;
}

export const POSITIONING: readonly Positioning[] = [
  {
    icon: 'users',
    title: '对个人开发者友好，对企业级应用完备',
    text: '面向个人创作者的易用性，与企业级方案应有的完备度，不应二选一。因此我们降低接入门槛，同时把可靠性与权限体系做扎实。',
  },
  {
    icon: 'code',
    title: '支持个人开发者接入',
    text: '接口文档完全公开。自研机体、开源飞控改装机与教学用小型机体，只要满足接口约定即可纳入平台监管。',
  },
  {
    icon: 'building',
    title: '支持企业定制无人机 SaaS',
    text: '同一套内核可按组织方式重塑：角色划分、界面命名、审批流程以及与既有业务系统的对接，均可协商。',
  },
];

export interface StoryParagraph {
  text: string;
}

export const STORY: readonly StoryParagraph[] = [
  {
    text: '创始人 LumiNya 认为，低空经济这一命题被谈论得过于宏大 —— 宏伟的路线图与真正可用的工具之间，仍有相当距离。与其继续讨论规模，不如先做出一个可用的产品。',
  },
  {
    text: '起点是一台从零设计的 H 型 770 轴距无人机。在设计过程中我们逐渐明确：把一台机器做成产品，飞行能力只占一半；另一半是它由谁管理、状态如何查看、出问题时如何查证。',
  },
  {
    text: '路线便沿此判断展开 —— 从机体向上延伸出无人机低空经济监管平台与配套软硬件。我们希望把它做成安全可靠、操作简易、详略得当、全程可追溯的系统，让各类低空运营拥有可依托的基础。',
  },
  {
    text: '目前平台已上线运行，机队管理、实时监管、权限与审计均已就位。我们不销售无人机整机：先把「管得清」做好，比多售出几台机器更为要紧。',
  },
];

export interface TimelineItem {
  phase: string;
  title: string;
  text: string;
}

export const TIMELINE: readonly TimelineItem[] = [
  { phase: '起点', title: 'H 型 770 轴距四轴', text: '整机构架、动力与飞控集成均由我们独立完成，也由此确定「做产品，而非做玩具」的方向。' },
  { phase: '延伸', title: '监管平台', text: '将机队、遥测、权限、告警与审计纳入同一套系统，浏览器直连机体，不设中间环节。' },
  { phase: '配套', title: '软硬件与接入文档', text: '公开机体接入接口，使自研与第三方机体均可纳入监管，并在需要时配套相应硬件。' },
  { phase: '现在', title: '开放接入与定制', text: '向个人开发者开放接入路径，为企业提供私有化部署与定制 SaaS。' },
];

export interface ValueItem {
  icon: IconName;
  title: string;
  text: string;
  evidence: string;
}

export const VALUES: readonly ValueItem[] = [
  {
    icon: 'shield',
    title: '安全可靠',
    text: '链路状态变化即如实呈现；权限判断仅在服务端执行，前端隐藏仅是体验层面的一环。',
    evidence: '断链、凭据失效与慢响应均有明确状态提示，不会以历史数据维持「正常」的表象。',
  },
  {
    icon: 'sliders',
    title: '操作简易',
    text: '高频操作一屏完成，危险动作单独区分并要求确认。',
    evidence: '起飞与降落前先执行条件检查清单；红色仅用于急停、解锁、重启一类不可逆操作。',
  },
  {
    icon: 'scale',
    title: '详略得当',
    text: '总览仅呈现判断所需的数据，细节保留给真正需要它的角色。',
    evidence: '链路质量在总览中以文字分级呈现，精确数值置于悬停提示与单机管理页。',
  },
  {
    icon: 'audit',
    title: '全程可追溯',
    text: '从建档、授权、指令下发到事后查证，每一步均留档。',
    evidence: '审计日志记录操作人、操作时间与具体行为，默认保留 180 天。',
  },
];

export interface BrandNote {
  title: string;
  paragraphs: readonly string[];
}

export const BRAND_NOTE: BrandNote = {
  title: '关于商标',
  paragraphs: [
    'Havorix™ 是本项目的品牌标识，商标注册申请中。在注册完成之前，我们统一使用 ™ 标记，不使用注册商标符号。',
    '若您在别处见到声称 Havorix 商标已完成注册的表述，与我们无关；也欢迎来信告知。',
  ],
};

export interface AboutCta {
  eyebrow: string;
  title: string;
  text: string;
  actions: readonly ActionLink[];
}

export const ABOUT_CTA: AboutCta = {
  eyebrow: '一起把基础做扎实',
  title: '有场景、有疑问，都欢迎直接沟通',
  text: '我们关心的是这套系统能否真正投入使用。请将您的机体情况、应用场景或顾虑写下来，这比客套话更有价值。',
  actions: [
    { label: '联系我们', to: '/contact', variant: 'primary' },
    { label: '查看工程动态', to: '/news', variant: 'secondary' },
  ],
};
